import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { ShoppingBag, MessageCircle, Search, Eye } from 'lucide-react';
import { motion } from 'framer-motion';
import { resolveImgUrl } from '@/utils/imgUrl';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const KATEGORI = ['Semua', 'Buku', 'Seragam', 'Perlengkapan', 'Makanan', 'Lainnya'];

export default function TokoPage() {
  const [produk, setProduk] = useState([]);
  const [filter, setFilter] = useState('Semua');
  const [search, setSearch] = useState('');
  const [settings, setSettings] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/produk`).then(r => setProduk(r.data)).catch(() => {}),
      axios.get(`${API}/settings`).then(r => setSettings(r.data)).catch(() => {}),
    ]);
  }, []);

  const filtered = produk.filter(p => {
    const matchKat = filter === 'Semua' || p.kategori === filter;
    const matchSearch = p.nama?.toLowerCase().includes(search.toLowerCase());
    return matchKat && matchSearch;
  });

  const rupiah = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n);

  const handleOrder = (p) => {
    const wa = p.whatsapp || settings.whatsapp_pesantren || '6281234567890';
    const msg = encodeURIComponent(`Assalamualaikum, saya ingin memesan produk:\n*${p.nama}*\nHarga: ${rupiah(p.harga)}`);
    window.open(`https://wa.me/${wa}?text=${msg}`, '_blank');
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 px-6 pt-12 pb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center">
              <ShoppingBag size={20} className="text-white" />
            </div>
            <div>
              <h1 className="font-playfair text-xl font-bold text-white">Toko ZahBaq</h1>
              <p className="text-emerald-300 text-xs">Produk & kebutuhan pesantren</p>
            </div>
          </div>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Cari produk..." data-testid="toko-search"
              className="w-full pl-9 pr-4 py-2.5 bg-white/10 text-white placeholder-emerald-300 rounded-xl text-sm border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/30"
            />
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white border-b border-gray-100 px-4 py-3">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
            {KATEGORI.map(k => (
              <button key={k} onClick={() => setFilter(k)}
                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                  filter === k ? 'bg-emerald-500 text-white shadow-emerald' : 'bg-gray-100 text-gray-600 hover:bg-emerald-50'
                }`}>{k}</button>
            ))}
          </div>
        </div>

        <div className="p-4 grid grid-cols-2 gap-3">
          {filtered.length === 0 && (
            <div className="col-span-2 text-center py-12 text-gray-400 text-sm">
              {produk.length === 0 ? 'Belum ada produk tersedia' : 'Produk tidak ditemukan'}
            </div>
          )}
          {filtered.map((p, i) => (
            <motion.div key={p.id} data-testid={`produk-${p.id}`}
              initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              className="bg-white rounded-2xl overflow-hidden shadow-card">
              {(p.fotos?.[0] || p.foto) ? (
                <img src={resolveImgUrl(p.fotos?.[0] || p.foto)} alt={p.nama} className="w-full h-32 object-cover" />
              ) : (
                <div className="w-full h-32 bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center">
                  <ShoppingBag size={28} className="text-emerald-300" />
                </div>
              )}
              <div className="p-3">
                <p className="text-[10px] text-emerald-600 font-semibold uppercase tracking-wider">{p.kategori}</p>
                <h3 className="font-semibold text-gray-800 text-sm leading-tight mt-0.5 line-clamp-2">{p.nama}</h3>
                <p className="font-bold text-emerald-700 text-base mt-1.5">{rupiah(p.harga)}</p>
                <div className="flex gap-1.5 mt-2.5">
                  <button
                    data-testid={`detail-${p.id}`}
                    onClick={() => navigate(`/toko/${p.id}`, { state: { produk: p } })}
                    className="flex-1 flex items-center justify-center gap-1 border border-emerald-400 text-emerald-700 py-1.5 rounded-xl text-xs font-semibold transition-all hover:bg-emerald-50"
                  >
                    <Eye size={12} /> Lihat Detail
                  </button>
                  <button data-testid={`order-${p.id}`} onClick={() => handleOrder(p)}
                    className="flex-1 flex items-center justify-center gap-1 bg-emerald-500 hover:bg-emerald-600 text-white py-1.5 rounded-xl text-xs font-semibold transition-all shadow-emerald">
                    <MessageCircle size={12} /> Pesan WA
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
